import { Box, SimpleGrid, useMediaQuery } from '@chakra-ui/react';
import type React from 'react';
import { useMemo } from 'react';
import DailyCollectionBarChart from '@/components/Organisms/DailyCollectionBarChart';
import MonthlyCollectionBarChart from '@/components/Organisms/MonthlyCollectionBarChart';
import MonthlyPayorCollectionLineChart from '@/components/Organisms/MonthlyPayorCollectionLineChart';
import PayorCollectionDonutChart from '@/components/Organisms/PayorCollectionDonutChart';
import ProductCollectionDonutChart from '@/components/Organisms/ProductCollectionDonutChart';
import { useGetProductClinicPort } from '@/hooks/service-hooks/common';
import { useAuthStore, useClinicStore } from '@/store';
import type { CollectionsSummaryProps } from './CollectionsSummary.types';

const CollectionsSummary: React.FC<CollectionsSummaryProps> = ({ isLoading = false }) => {
	// Get user clinics and port
	const currentClinics = useClinicStore((state) => state.currentClinics);
	const user = useAuthStore((state) => state.user?.userInformation);
	const userPort = user?.portsAvailable || '';
	
	// Get clinic IDs
	const clinicIds = useMemo(() => {
		return currentClinics.map((clinic) => clinic.accountid);
	}, [currentClinics]);

	// Fetch products for user's clinics
	const { data: portProductData } = useGetProductClinicPort({
		clinicIds,
		port: userPort,
		enabled: clinicIds.length > 0,
	});

	// Build clinicsProductsFilter format: "clinicId1|product1,product2;clinicId2|product3,product4"
	const clinicsProductsFilter = useMemo(() => {
		if (!portProductData || portProductData.length === 0) {
			// Fallback: just clinic IDs with empty products
			return clinicIds.map((id) => `${id}|`).join(';');
		}

		return portProductData
			.map((productInfo) => {
				// Format: clinicId|qcode1,qcode2,qcode3
				return `${productInfo.accountId}|${productInfo.accountQCodes}`;
			})
			.join(';');
	}, [portProductData, clinicIds]);
	// Detect screen sizes for scroll control
	const [isAbove1020] = useMediaQuery(['(min-width: 1020px)']);
	const [isAbove1200] = useMediaQuery(['(min-width: 1200px)']);

	// Enable scroll between 1020px - 1200px, disable above 1200px
	const disableScroll = useMemo(() => {
		// If screen >= 1200px, disable scroll
		if (isAbove1200) return true;
		// If screen is between 1020px and 1200px, enable scroll (so return false for disableScroll)
		if (isAbove1020 && !isAbove1200) return false;
		// For other sizes, let the responsive minWidth handle it
		return false;
	}, [isAbove1020, isAbove1200]);

	return (
		<Box>
			{/* Charts Grid - Side by Side on larger screens, stacked on mobile */}
			<Box mb={6} ml={{ base: 0, lg: 7 }} mr={{ base: 0, lg: 7 }}>
			<SimpleGrid columns={{ base: 1, lg: 2 }} gap={5}>
				<Box width="100%">
					<MonthlyCollectionBarChart
						title="Monthly Collection Trend"
						isLoading={isLoading}
						height={450}
						disableScroll={disableScroll}
						showFilters={true}
						showTableMenu={true}
						clinicsProductsFilter={clinicsProductsFilter}
					/>
				</Box>
				<Box width="100%">
					<DailyCollectionBarChart
						title="Daily Collection Trends"
						isLoading={isLoading}
						height={450}
						disableScroll={disableScroll}
						showFilters={true}
						showTableMenu={true}
						clinicsProductsFilter={clinicsProductsFilter}
					/>
				</Box>
			</SimpleGrid>
			</Box>

			{/* Donut Charts Grid - Side by Side on larger screens, stacked on mobile */}
			<Box mb={6} ml={{ base: 0, lg: 7 }} mr={{ base: 0, lg: 7 }}>
			<SimpleGrid columns={{ base: 1, lg: 2 }} gap={5}>
				<Box width="100%">
					<PayorCollectionDonutChart
						title="Payor Collection Analysis"
						isLoading={isLoading}
						height={450}
						disableScroll={disableScroll}
						showFilters={true}
						showTableMenu={true}
						clinicsProductsFilter={clinicsProductsFilter}
					/>
				</Box>
				<Box width="100%">
					<ProductCollectionDonutChart
						title="Product Collection Analysis"
						isLoading={isLoading}
						height={450}
						disableScroll={disableScroll}
						showFilters={true}
						showTableMenu={true}
						clinicsProductsFilter={clinicsProductsFilter}
					/>
				</Box>
			</SimpleGrid>
			</Box>

		{/* Monthly Payor Collection Line Chart */}
		<Box ml={{ base: 0, lg: 7 }} mr={{ base: 0, lg: 7 }}>
			<MonthlyPayorCollectionLineChart
				title="Monthly Payor Collection Trend"
				isLoading={isLoading}
				height={450}
				showFilters={true}
				showTableMenu={true}
				clinicsProductsFilter={clinicsProductsFilter}
			/>
		</Box>
		</Box>
	);
};

export default CollectionsSummary;

